import React, { useCallback, useEffect, useRef, useState } from "react";
import Markdown from "markdown-to-jsx";
import { UserMessage } from "./UserMessage";
import { AgentMessage } from "./AgentMessage";
import { MessageAuthor } from "../../Models/MessageAuthor";
import { IChatMessage } from "../../Models/ChatMessage";
import { Link } from "react-router-dom";
import { Box, styled } from "@mui/material";
import { observer } from "mobx-react";
import type { KboFlowResearchPreview } from "services/AcxAgentService";
import { KboFlowResearchDrawerPreview } from "./KboFlowResearchDrawerPreview";

type Props = {
    author: MessageAuthor;
    fullscreen?: boolean;
    kboFlowPreview?: KboFlowResearchPreview;
    onOpenFullscreen?: () => void;
} & IChatMessage;

const INITIAL_REPORT_HEIGHT = 150;

function KboFlowResearchReport({ html }: { html: string }) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState(INITIAL_REPORT_HEIGHT);

    const syncHeight = useCallback(() => {
        const document = iframeRef.current?.contentDocument;
        if (!document) return;

        const { body, documentElement } = document;
        const nextHeight = Math.ceil(
            Math.max(
                body?.scrollHeight ?? 0,
                body?.offsetHeight ?? 0,
                documentElement?.scrollHeight ?? 0,
                documentElement?.offsetHeight ?? 0,
            ),
        );

        if (nextHeight > 0) {
            setHeight((currentHeight) =>
                currentHeight === nextHeight ? currentHeight : nextHeight,
            );
        }
    }, []);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        let resizeObserver: ResizeObserver | undefined;
        const imageLoadListeners: HTMLImageElement[] = [];

        const clearDocumentObservers = () => {
            resizeObserver?.disconnect();
            resizeObserver = undefined;
            for (const image of imageLoadListeners) {
                image.removeEventListener("load", syncHeight);
            }
            imageLoadListeners.length = 0;
        };

        const observeDocument = () => {
            clearDocumentObservers();

            const document = iframe.contentDocument;
            if (!document) return;

            syncHeight();

            if (typeof ResizeObserver !== "undefined") {
                resizeObserver = new ResizeObserver(syncHeight);
                if (document.body) resizeObserver.observe(document.body);
                if (document.documentElement) {
                    resizeObserver.observe(document.documentElement);
                }
            }

            for (const image of document.images) {
                image.addEventListener("load", syncHeight);
                imageLoadListeners.push(image);
            }
        };

        iframe.addEventListener("load", observeDocument);
        observeDocument();

        return () => {
            iframe.removeEventListener("load", observeDocument);
            clearDocumentObservers();
        };
    }, [html, syncHeight]);

    return (
        <AgentMessage
            item
            className="pendo-ignore"
            sx={{
                backgroundColor: "transparent",
                display: "block",
                padding: 0,
                width: "100%",
            }}
        >
            <Box
                component="iframe"
                ref={iframeRef}
                title="KBo research report"
                srcDoc={html}
                sandbox="allow-same-origin"
                scrolling="no"
                sx={{
                    border: 0,
                    display: "block",
                    height,
                    overflow: "hidden",
                    width: "100%",
                }}
            />
        </AgentMessage>
    );
}

const UlOverride = styled("ul")(({ theme }) => ({
    paddingLeft: theme.spacing(2),
}));

const OlOverride = styled("ol")(({ theme }) => ({
    paddingLeft: theme.spacing(2),
}));

function LinkOverride(props: React.PropsWithChildren<{ href: string }>) {
    return <Link className="pendo-ignore" to={props.href}>{props.children}</Link>;
}

//Tags that should be stripped from agent messages before rendering
//These are used for system instructions and should not be visible to users
const SYSTEM_TAGS = [
    'additional_instructions'
] as const;

function stripSystemTags(content: string): string {
    let processedContent = content;

    for (const tag of SYSTEM_TAGS) {
        const regex = new RegExp(`<${tag}>[\\s\\S]*?<\\/${tag}>`, 'g');
        processedContent = processedContent.replace(regex, '');
    }

    return processedContent;
}

function fixUnclosedMarkdownLinks(content: string): string {
    let processedContent = content;

    // Regular expression to find markdown links
    // Matches [text](url) pattern, including incomplete ones
    const linkRegex = /\[([^\]]*)\](\([^)]*)?$/;

    const match = content.match(linkRegex);

    if (match) {
        const partialUrl = match[2] || "(";

        // If we have an opening parenthesis but no closing one, add it
        if (partialUrl.startsWith("(") && !partialUrl.includes(")")) {
            processedContent = content + ")";
        }
    }

    return processedContent;
}

const Message: React.FC<Props> = observer((props) => {
    const isUserMessage = props.author === MessageAuthor.User;

    const MessageContent = isUserMessage ? UserMessage : AgentMessage;

    const rawContent = props.dynamicContent
        ? props.dynamicContent()
        : props.content;

    if (!isUserMessage && props.renderMode === "htmlDocument") {
        if (!props.fullscreen && props.onOpenFullscreen) {
            return (
                <AgentMessage
                    item
                    className="pendo-ignore"
                    sx={{
                        backgroundColor: "transparent",
                        display: "block",
                        padding: 0,
                        width: "100%",
                    }}
                >
                    <KboFlowResearchDrawerPreview
                        preview={props.kboFlowPreview}
                        onOpenFullscreen={props.onOpenFullscreen}
                    />
                </AgentMessage>
            );
        }

        return <KboFlowResearchReport html={rawContent} />;
    }

    const getContent = () => {
        if (isUserMessage) {
            return rawContent;
        }

        const withoutSystemTags = stripSystemTags(rawContent);
        return fixUnclosedMarkdownLinks(withoutSystemTags);
    };

    return (
        <MessageContent item className={"pendo-ignore"}>
            {!isUserMessage && (
                <Markdown
                    options={{
                        overrides: {
                            a: LinkOverride,
                            ul: UlOverride,
                            ol: OlOverride,
                        },
                    }}
                >
                    {getContent()}
                </Markdown>
            )}
            {isUserMessage && <React.Fragment>{getContent()}</React.Fragment>}
        </MessageContent>
    );
});

export default Message;
